import { CSSProperties, Ref, forwardRef, useCallback, useRef, useState } from 'react';

import { flip, offset, shift, useFloating } from '@floating-ui/react-dom';
import { RowTwoIcon } from 'icons';
import { FormattedMessage } from 'localization';
import { Editor, Element as SlateElement, Transforms } from 'slate';
import { ReactEditor, useSlate } from 'slate-react';
import { twMerge } from 'tailwind-merge';
import { Tooltip } from 'ui';
import { IconButton } from 'ui/atoms';
import useClickAway from 'ui/src/hooks/useClickAway';

import { CustomElement } from '../types';

const COLUMN_OPTIONS = [
  { count: 2, labelId: 'desk-app.rich-editor.columns-layout.equal-2' },
  { count: 3, labelId: 'desk-app.rich-editor.columns-layout.equal-3' },
];

const createColumnsNode = (count: number): CustomElement => {
  // columnWidths will be initialized in px by ColumnsComponent on first render
  return {
    type: 'columns',
    columnCount: count,
    columnWidths: [],
    children: Array.from({ length: count }, () => ({
      type: 'column' as const,
      children: [{ type: 'paragraph' as const, children: [{ text: '' }] }],
    })),
  } as any;
};

const insertColumns = (editor: Editor, count: number) => {
  const columnsNode = createColumnsNode(count);
  Transforms.insertNodes(editor, columnsNode);
  Transforms.insertNodes(editor, {
    type: 'paragraph',
    children: [{ text: '' }],
  });
  ReactEditor.focus(editor);
};

const isColumnsActive = (editor: Editor) => {
  const [match] = Editor.nodes(editor, {
    match: (n) => !Editor.isEditor(n) && SlateElement.isElement(n) && n.type === 'columns',
  });
  return !!match;
};

type InsertColumnsButtonProps = {
  title: string;
  withTitle?: boolean;
  onClose?: () => void;
};

const InsertColumnsButton = ({ title, withTitle, onClose }: InsertColumnsButtonProps) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const { refs, floatingStyles } = useFloating({
    placement: 'top',
    middleware: [offset(4), flip(), shift()],
  });

  const editor = useSlate();
  const containerRef = useRef<HTMLDivElement | null>(null);

  useClickAway(containerRef, () => {
    if (isOpen) {
      setIsOpen(false);
    }
  });

  const toggleOpen = useCallback(() => {
    if (onClose && isOpen) {
      onClose();
    }
    ReactEditor.focus(editor);
    setIsOpen((prevState) => !prevState);
  }, [setIsOpen, onClose, isOpen]);

  return (
    <div
      ref={(currentRef) => {
        containerRef.current = currentRef;
        refs.setReference(currentRef);
      }}
    >
      <Tooltip title={withTitle || isOpen ? '' : title} placement="top">
        <div className="relative">
          <IconButton
            open={isOpen || isColumnsActive(editor)}
            className={twMerge(
              'flex items-center h-9 w-full hover:bg-transparent active:bg-v2blueGray-100',
              !withTitle && 'justify-center w-9',
              withTitle &&
                'justify-start px-2 gap-2 active:bg-v2blueGray-100 hover:bg-v2blueGray-100',
              (isOpen || isColumnsActive(editor)) && 'bg-v2blueGray-100 hover:bg-v2blueGray-100',
            )}
            onMouseDown={(e) => {
              e.preventDefault();
              toggleOpen();
            }}
          >
            <RowTwoIcon className="w-7 h-7" />
            {withTitle && <span className="capitalize">{title}</span>}
          </IconButton>
        </div>
      </Tooltip>
      {isOpen && (
        <ColumnsPickerDropdown
          onSelect={(count) => {
            insertColumns(editor, count);
            setIsOpen(false);
            onClose?.();
          }}
          ref={refs.setFloating}
          style={{ ...floatingStyles, position: withTitle ? 'absolute' : 'fixed' }}
        />
      )}
    </div>
  );
};

const ColumnsPickerDropdown = forwardRef(
  (
    {
      onSelect,
      style,
    }: {
      onSelect: (count: number) => void;
      style?: CSSProperties;
    },
    ref?: Ref<HTMLDivElement>,
  ) => {
    return (
      <div
        ref={ref}
        style={style}
        className="flex flex-col min-w-[180px] rounded-[14px] bg-white shadow-generic-popup overflow-hidden"
      >
        <div className="px-2 py-[10px]">
          {COLUMN_OPTIONS.map((option) => (
            <button
              key={option.count}
              type="button"
              className="flex items-center gap-3 w-full px-3 py-2 rounded-lg text-sm text-v2blueGray-800 hover:bg-v2blueGray-100 transition-colors"
              onMouseDown={(e) => {
                e.preventDefault();
                onSelect(option.count);
              }}
            >
              <ColumnPreview count={option.count} />
              <span>
                <FormattedMessage id={option.labelId} />
              </span>
            </button>
          ))}
        </div>
      </div>
    );
  },
);

const ColumnPreview = ({ count }: { count: number }) => (
  <div className="flex gap-[2px] w-8 h-5">
    {Array.from({ length: count }, (_, i) => (
      <div key={i} className="flex-1 rounded-sm border border-v2blueGray-400" />
    ))}
  </div>
);

export { InsertColumnsButton };
